import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, AlertOctagon, Loader2, MapPin, Brush, Sparkles } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import CustomCursor from '@/components/CustomCursor';
import CodeRain from '@/components/CodeRain';
import {
  Field,
  GalleryInput,
  GalleryTextarea,
  GalleryCTAButton,
  FormDivider,
  isValidEmail,
} from '@/components/forms/GalleryFormPrimitives';
import AvatarUpload from '@/components/forms/AvatarUpload';
import WorkDropzone from '@/components/forms/WorkDropzone';
import {
  uploadFileToIntake,
  generateImageThumbnail,
  generateVideoThumbnail,
  readImageDims,
  readVideoMeta,
} from '@/lib/intakeUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILE_FIELDS = [
  ['artist_name', 'ARTIST NAME', 'text'],
  ['email', 'EMAIL', 'email'],
  ['location', 'LOCATION', 'text'],
  ['city', 'CITY', 'text'],
  ['country', 'COUNTRY', 'text'],
  ['main_medium_style', 'MAIN MEDIUM / STYLE', 'text'],
  ['main_social_media', 'MAIN SOCIAL MEDIA', 'text'],
  ['portfolio_link', 'PORTFOLIO LINK', 'url'],
  ['website', 'WEBSITE', 'url'],
  ['instagram', 'INSTAGRAM', 'text'],
];

export default function ArtistIntakePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [intake, setIntake] = useState(null); // {valid, mode, artist, exhibition, constraints}

  const [profile, setProfile] = useState({});
  const [pfpFile, setPfpFile] = useState(null);
  const [works, setWorks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const mode = intake?.mode || 'full';

  // Load token (axios.get, with abortController so React 19 StrictMode double-invoke
  // is harmless and we only commit the latest response).
  useEffect(() => {
    if (!token) {
      setTokenError('missing');
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setTokenError('');
    axios
      .get(`${API}/artist-intake/${encodeURIComponent(token)}`, { signal: controller.signal })
      .then((res) => {
        setIntake(res.data);
        setProfile({
          artist_name: res.data.artist?.artist_name || '',
          email: res.data.artist?.email || '',
          location: res.data.artist?.location || '',
          city: res.data.artist?.city || '',
          country: res.data.artist?.country || '',
          main_medium_style: res.data.artist?.main_medium_style || '',
          main_social_media: res.data.artist?.main_social_media || '',
          portfolio_link: res.data.artist?.portfolio_link || '',
          website: res.data.artist?.website || '',
          instagram: res.data.artist?.instagram || '',
          artist_bio: res.data.artist?.artist_bio || '',
          artist_statement: res.data.artist?.artist_statement || '',
        });
        setLoading(false);
      })
      .catch((err) => {
        // ignore aborted requests (React 19 StrictMode + dev re-mount)
        if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        const status = err?.response?.status;
        if (status === 410) setTokenError('expired');
        else if (status === 404) setTokenError('invalid');
        else setTokenError('network');
        setLoading(false);
      });
    return () => controller.abort();
  }, [token]);

  const updateProfile = (k, v) => {
    setProfile((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
  };

  const validate = () => {
    const errs = {};
    if (mode === 'full' && !profile.artist_name?.trim()) {
      errs.artist_name = 'Please enter your artist name.';
    }
    if (profile.email && !isValidEmail(profile.email)) {
      errs.email = 'Please enter a valid email address.';
    }
    return errs;
  };

  const runUploads = async () => {
    let pfpResult = null;
    if (pfpFile) {
      pfpResult = await uploadFileToIntake({ token, kind: 'pfp', file: pfpFile });
    }
    const updatedWorks = [...works];
    for (let i = 0; i < updatedWorks.length; i += 1) {
      const w = updatedWorks[i];
      if (w.status === 'done') continue;
      updatedWorks[i] = { ...w, status: 'uploading', progress: 0, error: null };
      setWorks([...updatedWorks]);
      try {
        let meta = {};
        if (w.fileType === 'image') meta = await readImageDims(w.file);
        else if (w.fileType === 'video') meta = await readVideoMeta(w.file);

        let thumbFile = null;
        try {
          if (w.fileType === 'image') thumbFile = await generateImageThumbnail(w.file);
          else if (w.fileType === 'video') thumbFile = await generateVideoThumbnail(w.file);
        } catch (e) { /* non-blocking */ }

        const upload = await uploadFileToIntake({
          token,
          kind: 'work',
          file: w.file,
          temporaryClientId: w.id,
          onProgress: (p) => {
            updatedWorks[i] = { ...updatedWorks[i], progress: p };
            setWorks([...updatedWorks]);
          },
        });

        let thumbResult = null;
        if (thumbFile) {
          try {
            thumbResult = await uploadFileToIntake({
              token, kind: 'thumbnail', file: thumbFile, temporaryClientId: w.id,
            });
          } catch (e) { /* skip */ }
        }

        updatedWorks[i] = {
          ...updatedWorks[i],
          status: 'done',
          progress: 100,
          storage_path: upload.storage_path,
          file_url: upload.file_url,
          bucket_name: upload.bucket_name,
          thumbnail_path: thumbResult?.storage_path || null,
          thumbnail_url: thumbResult?.file_url || null,
          dimensions: meta.width ? { width: meta.width, height: meta.height } : updatedWorks[i].dimensions,
          duration_seconds: meta.duration_seconds || null,
        };
        setWorks([...updatedWorks]);
      } catch (err) {
        updatedWorks[i] = { ...updatedWorks[i], status: 'error', error: err?.message || 'Upload failed' };
        setWorks([...updatedWorks]);
        throw err;
      }
    }
    return { pfp: pfpResult, works: updatedWorks };
  };

  const finalSubmit = async ({ confirmEmpty } = {}) => {
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      toast.error('Please fix the highlighted fields.');
      return;
    }
    if (works.length === 0 && !pfpFile && !confirmEmpty) {
      const ok = window.confirm("You haven't uploaded any works yet. Submit anyway?");
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const { pfp, works: uploadedWorks } = await runUploads();
      const body = {
        ...profile,
        pfp: pfp ? {
          bucket_name: pfp.bucket_name,
          storage_path: pfp.storage_path,
          file_url: pfp.file_url,
        } : null,
        works: uploadedWorks
          .filter((w) => w.status === 'done')
          .map((w, idx) => ({
            title: w.title || null,
            description: w.description || null,
            artist_notes: null,
            bucket_name: w.bucket_name,
            storage_path: w.storage_path,
            file_url: w.file_url,
            thumbnail_storage_path: w.thumbnail_path || null,
            thumbnail_url: w.thumbnail_url || null,
            file_name: w.file.name,
            file_type: w.fileType,
            mime_type: w.file.type || null,
            file_size: w.file.size || null,
            width: w.dimensions?.width || null,
            height: w.dimensions?.height || null,
            duration_seconds: w.duration_seconds || null,
            sort_order: idx,
          })),
      };
      await axios.post(`${API}/artist-intake/${encodeURIComponent(token)}/submit`, body);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Submission failed.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const exhibitionTitle = useMemo(() => intake?.exhibition?.title || '', [intake]);

  // ─── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <PageShell>
        <div className="text-center py-32" data-testid="intake-loading">
          <Loader2 className="w-7 h-7 text-[#90E0EF] mx-auto animate-spin" />
          <p className="text-xs font-mono text-[#666] mt-4 tracking-[0.18em] uppercase">
            Loading your invitation…
          </p>
        </div>
      </PageShell>
    );
  }

  if (tokenError || !intake) {
    return (
      <PageShell>
        <InvalidLinkScreen onBack={() => navigate('/')} reason={tokenError} />
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <SuccessScreen onBack={() => navigate('/')} />
      </PageShell>
    );
  }

  const artist = intake.artist || {};
  const monogram = (artist.artist_name || artist.email || '?').trim().charAt(0).toUpperCase();
  const locationLine = [artist.city, artist.country].filter(Boolean).join(', ') || artist.location || '';

  return (
    <PageShell>
      <button
        data-testid="intake-back"
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase mb-12 transition-colors duration-300"
        style={{ cursor: 'pointer' }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Main Site
      </button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit">
          Artist Materials
        </h1>
        <p className="text-base sm:text-lg font-outfit text-white mt-2 tracking-tight">
          {exhibitionTitle ? `For ${exhibitionTitle}` : 'The Relative Gallery'}
        </p>
        <div className="mt-4 space-y-1 text-xs font-mono text-[#f6f6f6] tracking-[0.15em]">
          <p>THE RELATIVE GALLERY</p>
          <p>ARTIST INTAKE</p>
        </div>
      </motion.div>

      {/* Intro */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-12 space-y-5 text-sm sm:text-base font-mono text-[#f6f6f6] leading-relaxed"
      >
        <p>You have been invited to complete your artist materials for The Relative Gallery.</p>
        <p>
          Upload your profile image and the works you want our team to review. You can leave fields empty
          and return to details later if needed.
        </p>
      </motion.div>

      {/* Centered artist profile block */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.22 }}
        className="mt-10 border border-[#2a2a2a] bg-[#101010] p-6 sm:p-8 text-center shadow-[0_0_40px_rgba(144,224,239,0.04)]"
        data-testid="artist-profile-block"
      >
        <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto border border-[#222] bg-[#070707] overflow-hidden flex items-center justify-center">
          {artist.pfp_url ? (
            <img src={artist.pfp_url} alt={artist.artist_name || 'profile'} className="w-full h-full object-cover" />
          ) : (
            <span className="font-outfit text-3xl font-semibold text-[#90E0EF]">{monogram}</span>
          )}
        </div>
        <p className="mt-5 text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">Artist</p>
        <h2 className="mt-1 font-outfit text-2xl sm:text-3xl font-semibold text-white tracking-tight break-words">
          {artist.artist_name || 'Welcome'}
        </h2>
        {artist.email && (
          <p className="mt-1 text-xs sm:text-sm font-mono text-[#aaa] break-all">{artist.email}</p>
        )}

        {(artist.main_medium_style || locationLine) && (
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] font-mono text-[#888]">
            {artist.main_medium_style && (
              <span className="inline-flex items-center gap-1.5">
                <Brush className="w-3 h-3 text-[#666]" />
                {artist.main_medium_style}
              </span>
            )}
            {locationLine && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-[#666]" />
                {locationLine}
              </span>
            )}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-center gap-1.5">
          <span className="text-[10px] font-mono px-2.5 py-1 border border-[#90E0EF]/40 text-[#90E0EF] uppercase tracking-[0.18em] bg-[#90E0EF]/5">
            Artist Intake
          </span>
          <span className="text-[10px] font-mono px-2.5 py-1 border border-[#333] text-[#aaa] uppercase tracking-[0.18em]">
            {mode === 'assets_only' ? 'Assets Submission' : 'Full Profile'}
          </span>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-16"
      >
        <FormDivider />

        <form
          onSubmit={(e) => { e.preventDefault(); finalSubmit(); }}
          className="space-y-12"
          data-testid="artist-intake-form"
          noValidate
        >
          {/* 1) Artist Profile (FIRST per spec) */}
          <section data-testid="section-profile">
            <h2 className="text-xl sm:text-2xl font-outfit font-semibold text-white tracking-tight mb-2">
              Artist Profile
            </h2>
            <p className="text-xs font-mono text-[#888] mb-5 tracking-[0.05em]">
              {mode === 'assets_only'
                ? 'Some details are already on file. You can update anything that has changed.'
                : 'Tell us who you are. Most fields are optional.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROFILE_FIELDS.map(([key, label, type]) => (
                <Field
                  key={key}
                  label={label}
                  required={mode === 'full' && key === 'artist_name'}
                  error={errors[key]}
                >
                  <GalleryInput
                    type={type}
                    data-testid={`intake-${key}`}
                    value={profile[key] || ''}
                    onChange={(e) => updateProfile(key, e.target.value)}
                    error={errors[key]}
                  />
                </Field>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5">
              <Field label="ARTIST BIO">
                <GalleryTextarea
                  rows={4}
                  data-testid="intake-artist_bio"
                  value={profile.artist_bio || ''}
                  onChange={(e) => updateProfile('artist_bio', e.target.value)}
                  placeholder="Background, exhibitions, professional experience"
                />
              </Field>
              <Field label="ARTIST STATEMENT">
                <GalleryTextarea
                  rows={5}
                  data-testid="intake-artist_statement"
                  value={profile.artist_statement || ''}
                  onChange={(e) => updateProfile('artist_statement', e.target.value)}
                  placeholder="Your practice, themes, and ideas"
                />
              </Field>
            </div>
          </section>

          {/* 2) Profile Image */}
          <section data-testid="section-pfp">
            <h2 className="text-xl sm:text-2xl font-outfit font-semibold text-white tracking-tight mb-2">
              Profile Image
            </h2>
            <p className="text-xs font-mono text-[#888] mb-5 tracking-[0.05em]">
              Upload a profile image or portrait we can use internally for your artist profile.
            </p>
            <AvatarUpload
              value={pfpFile}
              existingUrl={artist.pfp_url}
              onChange={setPfpFile}
            />
          </section>

          {/* 3) Artwork / Collection Upload */}
          <section data-testid="section-works">
            <h2 className="text-xl sm:text-2xl font-outfit font-semibold text-white tracking-tight mb-2">
              Artwork / Collection Upload
            </h2>
            <p className="text-xs font-mono text-[#888] mb-5 tracking-[0.05em]">
              Drop your images or videos here. You can add an optional title and description for each file.
            </p>
            <WorkDropzone entries={works} onChange={setWorks} disabled={submitting} />
          </section>

          {/* 4) Submit */}
          <div className="pt-2">
            <GalleryCTAButton
              type="submit"
              loading={submitting}
              data-testid="intake-submit-btn"
            >
              {submitting ? 'SUBMITTING…' : 'SUBMIT MATERIALS'}
            </GalleryCTAButton>
          </div>
        </form>
      </motion.div>

      <div className="mt-16 pt-8 border-t border-[#1a1a1a]">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Main Site
        </button>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="bg-[#050505] min-h-screen text-[#F5F5F5]">
      <CustomCursor />
      <CodeRain />
      <div className="relative z-10 px-6 sm:px-10 py-12 max-w-4xl mx-auto">
        {children}
      </div>
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: '#111111',
            border: '1px solid #333333',
            color: '#F5F5F5',
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '12px',
          },
        }}
      />
    </div>
  );
}

function SuccessScreen({ onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="intake-success"
      className="py-12 space-y-5 text-sm sm:text-base font-mono text-[#f6f6f6] leading-relaxed"
    >
      <div className="flex items-center gap-2 text-[#90E0EF]">
        <Sparkles className="w-4 h-4" />
        <span className="text-[10px] font-mono tracking-[0.25em] uppercase">Submitted</span>
      </div>
      <h1 className="text-white font-outfit text-2xl sm:text-3xl font-semibold tracking-tight">
        Thank you. Your artist materials have been submitted.
      </h1>
      <p>The Relative Gallery team will review your profile and works.</p>
      <p className="mt-8">
        Best,<br />
        <strong className="text-white">The Relative Gallery Team</strong>
      </p>
      <div className="pt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Main Site
        </button>
      </div>
    </motion.div>
  );
}

function InvalidLinkScreen({ onBack, reason }) {
  const headline = reason === 'expired'
    ? 'This artist link is no longer active.'
    : reason === 'network'
      ? "We couldn't load your invitation."
      : reason === 'missing'
        ? 'No artist link was provided.'
        : 'This artist link is invalid or has expired.';
  const helper = reason === 'network'
    ? 'Please check your internet connection and refresh, or contact The Relative Gallery team.'
    : 'Please contact The Relative Gallery team if you need a new link.';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="intake-invalid"
      className="py-20 text-center"
    >
      <AlertOctagon className="w-7 h-7 text-[#90E0EF]/70 mx-auto" />
      <h1 className="mt-5 font-outfit text-2xl sm:text-3xl font-semibold text-white tracking-tight">
        {headline}
      </h1>
      <p className="mt-3 text-sm font-mono text-[#888] max-w-lg mx-auto leading-relaxed">
        {helper}
      </p>
      <p className="mt-1 text-sm font-mono text-[#888]">
        <a href="mailto:therelativegallery@gmail.com" className="text-[#90E0EF] hover:text-white transition-colors">
          therelativegallery@gmail.com
        </a>
      </p>
      <div className="mt-10">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Main Site
        </button>
      </div>
    </motion.div>
  );
}
